import React from 'react';
import { Box, Tabs, Tab, Card, Typography, Button, alpha } from '@mui/material';
import { TransactionsDataTable } from 'features/transactions';
import { WithdrawsDataTable } from 'features/withdrawals';
import { useListData } from 'features/transactions/hooks';
import useWithdraw from 'features/withdrawals/hooks/use-withdraw';
import PermissionBlur from 'features/gateways/components/permission-blur';
import { useGateway } from 'hooks';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabTemplate(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            sx={{ height: '100%' }}
            {...other}>
            {value === index && <Box>{children}</Box>}
        </Box>
    );
}

export default function TransactionsAndWithdrawTab({
    showTransaction,
    showWithdraw,
}: {
    showTransaction: boolean;
    showWithdraw: boolean;
}) {
    const gateway = useGateway();
    const navigate = useNavigate();
    const transactionData = useListData({
        limitCount: 5,
        enabled: showTransaction,
    });
    const withdrawData = useWithdraw({
        limitCount: 5,
        enabled: showWithdraw,
    });

    const preferredTab = showTransaction ? 0 : showWithdraw ? 1 : 0;
    const [value, setValue] = React.useState(preferredTab);

    React.useEffect(() => {
        setValue(preferredTab);
    }, [preferredTab]);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const currentAllowed = value === 0 ? showTransaction : showWithdraw;
    const currentRoute = value === 0 ? 'transaction' : 'withdraw';

    return (
        <Card
            elevation={0}
            sx={(theme) => ({
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                borderRadius: '18px',
                border: 0,
                background: `linear-gradient(160deg, ${alpha(
                    theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.022 : 0.012,
                )} 0%, transparent 38%), ${theme.palette.background.paper}`,
                boxShadow:
                    theme.palette.mode === 'light'
                        ? '0 12px 36px rgba(17, 24, 39, 0.045)'
                        : `0 10px 30px ${alpha(theme.palette.common.black, 0.12)}`,
            })}>
            <Box
                sx={{
                    px: { xs: 1.75, md: 2.15 },
                    pt: 1.65,
                    pb: 1.1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.25,
                }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        className="font-yekan-heavy"
                        sx={{ fontSize: 14.5, color: 'text.primary' }}>
                        آخرین فعالیت‌ها
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.15,
                            fontSize: 10.5,
                            lineHeight: 1.65,
                            color: 'text.secondary',
                        }}>
                        آخرین تراکنش‌ها و تسویه حساب‌های این درگاه
                    </Typography>
                </Box>

                <Button
                    size="small"
                    disabled={!currentAllowed}
                    onClick={() =>
                        currentAllowed &&
                        navigate(`/${gateway?.website}/${currentRoute}`)
                    }
                    sx={(theme) => {
                        const isDark = theme.palette.mode === 'dark';
                        const actionColor = isDark
                            ? theme.palette.primary.light
                            : theme.palette.primary.dark;

                        return {
                            px: 1.2,
                            minWidth: 0,
                            height: 34,
                            borderRadius: '10px',
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: actionColor,
                            bgcolor: alpha(
                                theme.palette.primary.main,
                                isDark ? 0.14 : 0.075,
                            ),
                            boxShadow: isDark
                                ? `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.08)}`
                                : `0 3px 10px ${alpha(theme.palette.primary.main, 0.06)}`,
                            '&:hover': {
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    isDark ? 0.21 : 0.12,
                                ),
                                color: actionColor,
                            },
                            '&.Mui-disabled': {
                                color: alpha(theme.palette.text.secondary, 0.45),
                                bgcolor: alpha(theme.palette.action.disabled, 0.045),
                            },
                        };
                    }}>
                    مشاهده همه
                </Button>
            </Box>

            <Box sx={{ px: { xs: 1.25, md: 1.75 }, pb: 1.05 }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    sx={(theme) => {
                        const isDark = theme.palette.mode === 'dark';
                        const selectedColor = isDark
                            ? theme.palette.primary.light
                            : theme.palette.primary.dark;

                        return {
                            minHeight: 38,
                            width: 'fit-content',
                            maxWidth: '100%',
                            bgcolor: alpha(
                                theme.palette.primary.main,
                                isDark ? 0.095 : 0.045,
                            ),
                            borderRadius: '11px',
                            p: 0.35,
                            '& .MuiTabs-indicator': { display: 'none' },
                            '& .MuiTabs-flexContainer': { gap: 0.4 },
                            '& .MuiTab-root': {
                                minHeight: 32,
                                minWidth: { xs: 104, sm: 128 },
                                px: 1.25,
                                py: 0.45,
                                borderRadius: '8px',
                                fontSize: 11.5,
                                fontWeight: 700,
                                color: isDark
                                    ? alpha(theme.palette.common.white, 0.58)
                                    : theme.palette.text.secondary,
                                transition:
                                    'background-color .16s ease, color .16s ease, box-shadow .16s ease',
                                '&:hover': {
                                    color: selectedColor,
                                    bgcolor: alpha(
                                        theme.palette.primary.main,
                                        isDark ? 0.09 : 0.055,
                                    ),
                                },
                                '&.Mui-selected': {
                                    color: selectedColor,
                                    bgcolor: alpha(
                                        theme.palette.primary.main,
                                        isDark ? 0.19 : 0.095,
                                    ),
                                    boxShadow: isDark
                                        ? `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.09)}`
                                        : `0 3px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                                },
                            },
                        };
                    }}>
                    <Tab label="تراکنش ها" />
                    <Tab label="تسویه حساب ها" />
                </Tabs>
            </Box>

            <TabTemplate value={value} index={0}>
                <PermissionBlur
                    allowed={showTransaction}
                    label="دسترسی تراکنش‌ها برای شما فعال نیست"
                    minHeight={300}
                    borderRadius={0}>
                    <TransactionsDataTable
                        transactionData={transactionData}
                        isHeaderDark
                        isDashboard
                    />
                </PermissionBlur>
            </TabTemplate>

            <TabTemplate value={value} index={1}>
                <PermissionBlur
                    allowed={showWithdraw}
                    label="دسترسی تسویه حساب‌ها برای شما فعال نیست"
                    minHeight={300}
                    borderRadius={0}>
                    <WithdrawsDataTable
                        withdrawData={withdrawData}
                        isHeaderDark
                        isDashboard
                    />
                </PermissionBlur>
            </TabTemplate>
        </Card>
    );
}
